import React, { useState, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fieldErrorMessage } from './fieldTypes';
import api from './config/api';
import NotFound from './components/NotFound';
import CollectionsHome from './components/CollectionsHome';
import SearchPage from './components/SearchPage';
import InventoryDetail from './components/InventoryDetail';
import CreateInventoryModal from './components/CreateInventoryModal';
import ItemTypeList from './components/ItemTypeList';
import ItemTypeIntegration from './components/ItemTypeIntegration';
import ItemTypeConversion from './components/ItemTypeConversion';
import CreateItemTypeDialog from './components/CreateItemTypeDialog';
import ItemTypeEditor from './components/ItemTypeEditor';
import LocationTypeList from './components/LocationTypeList';
import CreateLocationTypeModal from './components/CreateLocationTypeModal';
import AIConfiguration from './components/AIConfiguration';
import CompletionAgentConfiguration from './components/CompletionAgentConfiguration';
import BacklogList from './components/BacklogList';
import EditInventoryItemTypesModal from './components/EditInventoryItemTypesModal';
import AccountsAdmin from './components/AccountsAdmin';
import InviteModal from './components/InviteModal';
import ShareModal from './components/ShareModal';
import SourceCredits from './components/SourceCredits';
import { errorMessage } from './errors';
import AppShell from './shell/AppShell';
import { SectionHome, SectionLayout } from './settings/SectionLayout';
import AccountSettings from './settings/AccountSettings';
import { useOnChanged } from './shell/changes';
import { Spinner, useConfirm, useToast } from './ui';

function App({ user, onLogout }) {
  const [inventories, setInventories] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [itemTypesLoaded, setItemTypesLoaded] = useState(false);
  const [locationTypes, setLocationTypes] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateTypeModal, setShowCreateTypeModal] = useState(false);
  const [showCreateLocationTypeModal, setShowCreateLocationTypeModal] = useState(false);
  const [editingLocationType, setEditingLocationType] = useState(null);
  const [editingInventoryItemTypes, setEditingInventoryItemTypes] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [sharingInventory, setSharingInventory] = useState(null);
  const [showAllInventories, setShowAllInventories] = useState(false);
  const [loading, setLoading] = useState(true);
  const isAdmin = user.role === 'admin';
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();
  // Back to the collections when the collection whose page is open goes away.
  const leavePage = (inv) => {
    if (location.pathname.startsWith(`/c/${inv.id}/`) || location.pathname === `/c/${inv.id}`) navigate('/', { replace: true });
  };
  // The sidebar lists the caller's own collections and those shared with
  // them, whatever "Show all accounts" says.
  const [myInventories, setMyInventories] = useState([]);
  // Another account's collection an admin opened by URL (see CollectionRoute).
  const [otherInventory, setOtherInventory] = useState(undefined);

  useEffect(() => {
    fetchItemTypes();
    fetchLocationTypes();
  }, []);

  useEffect(() => {
    fetchInventories().catch(() => {});
  }, [showAllInventories]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInventories = async () => {
    try {
      const all = isAdmin && showAllInventories;
      const response = await api.get(all ? '/inventories?scope=all' : '/inventories');
      setInventories(response.data);
      if (!all) setMyInventories(response.data);
      else api.get('/inventories').then((r) => setMyInventories(r.data)).catch(() => {});
      return response.data;
    } catch (error) {
      console.error('Error fetching inventories:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Items were added or removed somewhere: the list carries the counts.
  useOnChanged('collections', () => { fetchInventories().catch(() => {}); });
  useOnChanged('item-types', () => { fetchItemTypes(); });   // a type made from a model by the New collection dialog

  const fetchItemTypes = async () => {
    try {
      const response = await api.get('/item-types');
      setItemTypes(response.data);
    } catch (error) {
      console.error('Error fetching item types:', error);
    } finally {
      setItemTypesLoaded(true);
    }
  };

  const fetchLocationTypes = async () => {
    try {
      const response = await api.get('/location-types');
      setLocationTypes(response.data);
    } catch (error) {
      console.error('Error fetching location types:', error);
    }
  };

  const createInventory = async (inventoryData) => {
    try {
      const response = await api.post('/inventories', inventoryData);
      const created = { item_count: 0, ...response.data };
      setInventories([...inventories, created]);
      setMyInventories((list) => [...list, created]);
      setShowCreateModal(false);
      toast.success(t('shell.collectionCreated', { name: response.data.name }));
    } catch (error) {
      console.error('Error creating inventory:', error);
      console.error('Error details:', error.response?.data);
      toast.error(t('errors.createInventory', { error: errorMessage(t, error) }));
    }
  };

  // Asks, deletes, and leaves the collection's page if it was open.
  const deleteInventory = async (inv) => {
    if (!(await confirm({
      title: t('collections.confirmDeleteTitle'),
      message: t('collections.confirmDelete', { name: inv.name }),
      confirmLabel: t('collections.deleteNamed', { name: inv.name }),
      danger: true,
    }))) return;
    try {
      await api.delete(`/inventories/${inv.id}`);
      setInventories((list) => list.filter(other => other.id !== inv.id));
      setMyInventories((list) => list.filter(other => other.id !== inv.id));
      toast.success(t('shell.collectionDeleted', { name: inv.name }));
      leavePage(inv);
    } catch (error) {
      console.error('Error deleting inventory:', error);
      toast.error(t('errors.deleteInventory', { error: errorMessage(t, error) }));
    }
  };

  // A collection shared with the caller: stop seeing it.
  const leaveInventory = async (inv) => {
    if (!(await confirm({
      title: t('collections.confirmLeaveTitle'),
      message: t('collections.confirmLeave', { name: inv.name }),
      confirmLabel: t('collections.leave'),
      danger: true,
    }))) return;
    try {
      await api.delete(`/inventories/${inv.id}/shares/${user.id}`);
      fetchInventories().catch(() => {});
      toast.success(t('collections.left', { name: inv.name }));
      leavePage(inv);
    } catch (error) {
      console.error('Error leaving inventory:', error);
      toast.error(t('collections.errors.leave', { error: errorMessage(t, error) }));
    }
  };

  // From a model, in the "New item type" dialog (a blank type is made on its editor page).
  const createItemType = async (itemTypeData) => {
    try {
      const response = await api.post('/item-types', itemTypeData);
      setItemTypes((list) => [...list, response.data]);
      setShowCreateTypeModal(false);
      toast.success(t('itemTypeForm.created', { name: response.data.name }));
    } catch (error) {
      console.error('Error creating item type:', error);
      console.error('Error details:', error.response?.data);
      toast.error(t('errors.createItemType', { error: fieldErrorMessage(t, error, itemTypeData.fields) || errorMessage(t, error) }));
    }
  };

  // The editor page saved a type: into the list (or the list again, after a
  // migration), and the collections again, for the types they carry.
  const itemTypeSaved = (saved, { refetch = false } = {}) => {
    if (refetch) fetchItemTypes();
    else setItemTypes((list) => (list.some(type => type.id === saved.id)
      ? list.map(type => (type.id === saved.id ? saved : type))
      : [...list, saved]));
    fetchInventories().catch(() => {});
  };

  const deleteItemType = async (id) => {
    try {
      await api.delete(`/item-types/${id}`);
      setItemTypes(itemTypes.filter(type => type.id !== id));
    } catch (error) {
      console.error('Error deleting item type:', error);
      toast.error(t('errors.deleteItemType', { error: errorMessage(t, error) }));
    }
  };

  const createLocationType = async (locationTypeData) => {
    try {
      const response = await api.post('/location-types', locationTypeData);
      setLocationTypes([...locationTypes, response.data]);
      setShowCreateLocationTypeModal(false);
      setEditingLocationType(null);
    } catch (error) {
      console.error('Error creating location type:', error);
      console.error('Error details:', error.response?.data);
      toast.error(t('errors.createLocationType', { error: errorMessage(t, error) }));
    }
  };

  const updateLocationType = async (locationTypeData) => {
    try {
      const response = await api.put(`/location-types/${editingLocationType.id}`, locationTypeData);
      setLocationTypes(locationTypes.map(type => 
        type.id === editingLocationType.id ? response.data : type
      ));
      setShowCreateLocationTypeModal(false);
      setEditingLocationType(null);
    } catch (error) {
      console.error('Error updating location type:', error);
      console.error('Error details:', error.response?.data);
      toast.error(t('errors.updateLocationType', { error: errorMessage(t, error) }));
    }
  };

  const deleteLocationType = async (id) => {
    try {
      await api.delete(`/location-types/${id}`);
      setLocationTypes(locationTypes.filter(type => type.id !== id));
    } catch (error) {
      console.error('Error deleting location type:', error);
      toast.error(t('errors.deleteLocationType', { error: errorMessage(t, error) }));
    }
  };

  const handleLocationTypeAction = (locationTypeData) => (
    editingLocationType ? updateLocationType(locationTypeData) : createLocationType(locationTypeData)
  );

  if (loading) {
    return (
      <div className="app-loading">
        <Spinner size={24} label={t('common.loading')} />
      </div>
    );
  }

  const updateInventory = (updated) => {
    // A single collection's answer has no item count: keep the list's.
    const merge = (inv) => (inv.id === updated.id ? { item_count: inv.item_count, cover_ids: inv.cover_ids, ...updated } : inv);
    setInventories((list) => list.map(merge));
    setMyInventories((list) => list.map(merge));
  };
  const sidebarInventories = isAdmin && showAllInventories ? myInventories : inventories;
  const knownInventories = otherInventory && !inventories.some(inv => inv.id === otherInventory.id)
    ? [...inventories, otherInventory] : inventories;
  const section = (name, page) => <SectionLayout section={name}>{page}</SectionLayout>;
  const adminOnly = (page) => (isAdmin ? section('admin', page) : <Navigate to="/" replace />);

  const routes = (
    <Routes>
      <Route
        path="/"
        element={(
          <CollectionsHome
            inventories={inventories}
            isAdmin={isAdmin}
            showAll={showAllInventories}
            onShowAll={setShowAllInventories}
            onDelete={deleteInventory}
            onLeave={leaveInventory}
            onCreateNew={() => setShowCreateModal(true)}
            onEditItemTypes={setEditingInventoryItemTypes}
            onShare={setSharingInventory}
          />
        )}
      />
      <Route
        path="/c/:id/*"
        element={(
          <CollectionRoute inventories={inventories} isAdmin={isAdmin} onUpdate={updateInventory}
                           other={otherInventory} setOther={setOtherInventory}
                           actions={{ onDelete: deleteInventory, onLeave: leaveInventory, onShare: setSharingInventory, onEditItemTypes: setEditingInventoryItemTypes }} />
        )}
      />
      <Route path="/search" element={<SearchPage inventories={knownInventories} />} />
      <Route path="/settings" element={section('settings', <SectionHome section="settings" />)} />
      <Route
        path="/settings/account"
        element={section('settings', <AccountSettings user={user} onInvite={() => setShowInviteModal(true)} onLogout={onLogout} />)}
      />
      <Route
        path="/settings/item-types"
        element={section('settings', (
          <ItemTypeList
            itemTypes={itemTypes}
            onDelete={deleteItemType}
            onCreateNew={() => setShowCreateTypeModal(true)}
            onRefresh={fetchItemTypes}
          />
        ))}
      />
      <Route path="/settings/item-types/new" element={section('settings', <ItemTypeEditor itemTypes={itemTypes} loaded={itemTypesLoaded} onSaved={itemTypeSaved} />)} />
      <Route path="/settings/item-types/:id" element={section('settings', <ItemTypeEditor itemTypes={itemTypes} loaded={itemTypesLoaded} onSaved={itemTypeSaved} />)} />
      <Route path="/settings/item-types/:id/integration" element={section('settings', <ItemTypeIntegration itemTypes={itemTypes} />)} />
      <Route path="/settings/item-types/:id/convert" element={section('settings', <ItemTypeConversion itemTypes={itemTypes} loaded={itemTypesLoaded} onConverted={fetchItemTypes} />)} />
      <Route
        path="/settings/locations"
        element={section('settings', (
          <LocationTypeList
            locationTypes={locationTypes}
            onDelete={deleteLocationType}
            onCreateNew={() => setShowCreateLocationTypeModal(true)}
            onEdit={(locationType) => {
              setEditingLocationType(locationType);
              setShowCreateLocationTypeModal(true);
            }}
            onRefresh={() => { fetchLocationTypes(); fetchInventories().catch(() => {}); }}
            isAdmin={isAdmin}
          />
        ))}
      />
      <Route path="/admin" element={adminOnly(<SectionHome section="admin" />)} />
      <Route path="/admin/accounts" element={adminOnly(<AccountsAdmin currentUserId={user.id} onInvite={() => setShowInviteModal(true)} />)} />
      <Route path="/admin/backlog" element={adminOnly(<BacklogList />)} />
      <Route path="/admin/ai" element={adminOnly(<AIConfiguration />)} />
      <Route path="/admin/agents" element={adminOnly(<CompletionAgentConfiguration />)} />
      <Route path="/admin/*" element={isAdmin ? <NotFound /> : <Navigate to="/" replace />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );

  // The models of the member's types whose sources ask to be credited (books; video games: IGDB, §4b.4).
  const bookPresets = [...new Set(itemTypes.map(type => type.preset).filter(preset => preset === 'book' || preset === 'videogame'))];

  return (
    <AppShell
      user={user}
      inventories={sidebarInventories}
      allInventories={knownInventories}
      onNewCollection={() => setShowCreateModal(true)}
      onInvite={() => setShowInviteModal(true)}
      onLogout={onLogout}
      footer={(
        /* TMDB's terms of use: attribution on every page of the application. */
        <footer className="app-footer">
          {t('footer.tmdb')}{' '}
          <a href="https://www.themoviedb.org" target="_blank" rel="noopener noreferrer">themoviedb.org</a>
          {bookPresets.length > 0 && <>{' '}<SourceCredits presets={bookPresets} /></>}
        </footer>
      )}
    >
      {routes}

      {showInviteModal && (
        <InviteModal inventories={myInventories.filter(inv => inv.my_permission === 'owner' && inv.is_mine)}
                     onClose={() => setShowInviteModal(false)} />
      )}

      {sharingInventory && (
        <ShareModal inventory={sharingInventory} onClose={() => setSharingInventory(null)} />
      )}

      {showCreateModal && (
        <CreateInventoryModal
          onClose={() => setShowCreateModal(false)}
          onCreate={createInventory}
        />
      )}

      {showCreateTypeModal && (
        <CreateItemTypeDialog
          onClose={() => setShowCreateTypeModal(false)}
          onCreate={createItemType}
          itemTypes={itemTypes}
        />
      )}

      {showCreateLocationTypeModal && (
        <CreateLocationTypeModal
          onClose={() => {
            setShowCreateLocationTypeModal(false);
            setEditingLocationType(null);
          }}
          onCreate={handleLocationTypeAction}
          locationType={editingLocationType}
        />
      )}

      {editingInventoryItemTypes && (
        <EditInventoryItemTypesModal
          inventory={editingInventoryItemTypes}
          currentUserId={user.id}
          onClose={() => setEditingInventoryItemTypes(null)}
          onUpdate={(updatedInventory) => {
            updateInventory(updatedInventory);
            setEditingInventoryItemTypes(null);
          }}
        />
      )}
    </AppShell>
  );
}

// /c/:id/* — finds the collection in the list App already holds. An admin may
// open another account's collection by URL without "Show all accounts" ticked:
// it is then looked up in the full list. That one (`other`, undefined while
// not looked up yet) is kept by App, so the shell knows it too.
function CollectionRoute({ inventories, isAdmin, onUpdate, other, setOther, actions }) {
  const { t } = useTranslation();
  const { id } = useParams();
  const fromList = inventories.find(inv => String(inv.id) === id);

  useEffect(() => {
    if (fromList || !isAdmin) return;
    let cancelled = false;
    setOther(undefined);
    api.get('/inventories?scope=all')
      .then((r) => { if (!cancelled) setOther(r.data.find(inv => String(inv.id) === id) || null); })
      .catch(() => { if (!cancelled) setOther(null); });
    return () => { cancelled = true; };
  }, [id, fromList, isAdmin, setOther]);

  const inventory = fromList || (other && String(other.id) === id ? other : null);
  if (!inventory) {
    if (isAdmin && other === undefined) {
      return <Spinner size={24} label={t('common.loading')} />;
    }
    return <NotFound />;
  }
  return (
    <InventoryDetail
      key={inventory.id}
      inventory={inventory}
      actions={actions}
      onUpdate={(updated) => {
        if (fromList) onUpdate(updated);
        else setOther(updated);
      }}
    />
  );
}

export default App;
